import { Container, Graphics, Sprite, Texture, Assets, Rectangle as PIXI_Rectangle } from 'pixi.js';
import { GlowFilter } from 'pixi-filters';
import { assetLoader } from '../core/AssetLoader';
import { tween, delay } from '../core/TweenManager';
import { EngineFlare } from './EngineFlare';

// ============================================
// SVG SHIP TEXTURE SYSTEM
// ============================================

// Cache for loaded SVG textures
const svgTextureCache: Map<string, Texture> = new Map();
const svgLoadingPromises: Map<string, Promise<Texture | null>> = new Map();

// SVG ship metadata (dimensions and scales)
// NOTE: SVG ships are drawn pointing RIGHT (east), so we rotate -90° to point UP (north)
// After rotation: the LEFT side of the SVG becomes the BOTTOM (rear) of the ship
// thrustOffsetY is how far behind center the thrust should appear (in final UP-pointing coords)
//
// SCALE PHILOSOPHY: Capital ships (those with DRONE bays) must feel ENORMOUS.
// There is a deliberate visual gap between Tier 1 (skirmishers) and Tier 2 (capital).
// Seeing a Bulwark/Nest/Titan should be an "OH NO" moment for the player.
export const SVG_SHIP_META: Record<string, { width: number; height: number; scale: number; thrustOffsetY: number }> = {
  // === TIER 1: Skirmishers (no drone bays) ===
  // width/height MUST match SVG viewBox dimensions
  corvette:    { width: 166, height: 218, scale: 0.20, thrustOffsetY: 83 },
  frigate:     { width: 162, height: 224, scale: 0.35, thrustOffsetY: 81 },
  destroyer:   { width: 196, height: 164, scale: 0.50, thrustOffsetY: 98 },
  cruiser:     { width: 296, height: 211, scale: 0.55, thrustOffsetY: 148 },
  hauler:      { width: 191, height: 190, scale: 0.30, thrustOffsetY: 95 },

  // === TIER 2: Capital ships (DRONE bays — the "OH NO" tier) ===
  // width/height MUST match SVG viewBox dimensions
  battleship:  { width: 275, height: 222, scale: 1.10, thrustOffsetY: 137 },
  carrier:     { width: 251, height: 253, scale: 1.40, thrustOffsetY: 126 },
  dreadnought: { width: 285, height: 196, scale: 2.00, thrustOffsetY: 142 },
};

// SVG ships point RIGHT, game expects UP, so rotate -90 degrees
const SVG_ROTATION_OFFSET = -Math.PI / 2;

/**
 * Load an SVG texture for a ship class
 */
async function loadSVGTexture(shipClass: string): Promise<Texture | null> {
  if (svgTextureCache.has(shipClass)) {
    return svgTextureCache.get(shipClass)!;
  }

  if (svgLoadingPromises.has(shipClass)) {
    return svgLoadingPromises.get(shipClass)!;
  }

  const loadPromise = (async () => {
    try {
      const url = `/assets/ships/${shipClass}.svg`;
      const texture = await Assets.load<Texture>(url);
      if (texture) {
        svgTextureCache.set(shipClass, texture);
        return texture;
      }
    } catch (error) {
      // SVG not found, will use point-based fallback
    }
    return null;
  })();

  svgLoadingPromises.set(shipClass, loadPromise);
  return loadPromise;
}

/**
 * Check if SVG is cached for a ship class (synchronous)
 */
export function hasSVGCached(shipClass: string): boolean {
  return svgTextureCache.has(shipClass);
}

/**
 * Get cached SVG texture (synchronous, returns null if not cached)
 */
export function getSVGTexture(shipClass: string): Texture | null {
  return svgTextureCache.get(shipClass) || null;
}

/**
 * Preload all ship SVGs - call during boot
 */
export async function preloadShipSVGs(): Promise<void> {
  const shipClasses = Object.keys(SVG_SHIP_META);
  await Promise.all(shipClasses.map(async (sc) => {
    await loadSVGTexture(sc);
  }));
}

// Default ship color (teal from palette)
export const DEFAULT_SHIP_COLOR = 0x20a5a6;

// Tier-based glow settings (from asset specs)
// Same silhouette for all tiers - tier indicated by glow intensity
export interface TierGlowSettings {
  tightDistance: number;  // Tight glow distance
  tightStrength: number;  // Tight glow alpha
  wideDistance: number;   // Wide glow distance
  wideStrength: number;   // Wide glow alpha
  pulse: boolean;         // T4 gets subtle pulse
  brightnessBoost: number; // Extra brightness for T3/T4
}

export const TIER_GLOW_SETTINGS: Record<number, TierGlowSettings> = {
  1: { tightDistance: 3,  tightStrength: 0.15, wideDistance: 8,   wideStrength: 0.08, pulse: false, brightnessBoost: 0 },
  2: { tightDistance: 4,  tightStrength: 0.20, wideDistance: 10,  wideStrength: 0.10, pulse: false, brightnessBoost: 0 },
  3: { tightDistance: 5,  tightStrength: 0.25, wideDistance: 12,  wideStrength: 0.12, pulse: false, brightnessBoost: 0.10 },
  4: { tightDistance: 6,  tightStrength: 0.30, wideDistance: 15,  wideStrength: 0.15, pulse: true,  brightnessBoost: 0.15 },
};

// Preset colors for selection
export const SHIP_COLOR_PRESETS = [
  0x20a5a6, // Teal (default)
  0xdd5639, // Orange
  0xf5e8d1, // Cream
  0x66cc99, // Green
  0xcc6699, // Pink
  0x6699cc, // Blue
  0xffcc44, // Yellow
  0xaa66cc, // Purple
];

export interface ShipDesign {
  points: { x: number; y: number }[];
  thrustOffset: { x: number; y: number };
  scale: number;
}

// Ship designs - all drawn pointing UP (nose at top)
// Angular, squared silhouettes - lean and menacing
export const SHIP_DESIGNS: Record<string, ShipDesign> = {
  // Corvette class - keep as is, agile fighter with swept wings
  corvette: {
    scale: 1,
    thrustOffset: { x: 0, y: 20 },
    points: [
      { x: 0, y: -24 },    // Nose
      { x: -8, y: -8 },    // Left shoulder
      { x: -20, y: 20 },   // Left wing tip
      { x: -8, y: 14 },    // Left wing inner
      { x: 0, y: 20 },     // Back center
      { x: 8, y: 14 },     // Right wing inner
      { x: 20, y: 20 },    // Right wing tip
      { x: 8, y: -8 },     // Right shoulder
    ],
  },

  // Frigate class - angular attack ship, arrowhead shape
  // Sharp front tapering to angled rear with engine notch
  frigate: {
    scale: 1.4,
    thrustOffset: { x: 0, y: 22 },
    points: [
      { x: 0, y: -24 },     // Sharp nose point
      { x: -16, y: -6 },    // Left front angle
      { x: -20, y: 8 },     // Left mid hull (widest)
      { x: -16, y: 18 },    // Left rear taper
      { x: -8, y: 20 },     // Left engine outer
      { x: -6, y: 12 },     // Left engine notch inner
      { x: 0, y: 14 },      // Center notch bottom
      { x: 6, y: 12 },      // Right engine notch inner
      { x: 8, y: 20 },      // Right engine outer
      { x: 16, y: 18 },     // Right rear taper
      { x: 20, y: 8 },      // Right mid hull (widest)
      { x: 16, y: -6 },     // Right front angle
    ],
  },

  // Destroyer class - angular arrow shape, aggressive
  // Sharp pointed front, angular sides
  destroyer: {
    scale: 1.6,
    thrustOffset: { x: 0, y: 24 },
    points: [
      { x: 0, y: -28 },    // Sharp nose point
      { x: -10, y: -16 },  // Left nose angle
      { x: -10, y: -8 },   // Left upper hull
      { x: -20, y: -4 },   // Left wing point
      { x: -16, y: 8 },    // Left mid hull
      { x: -18, y: 20 },   // Left engine outer
      { x: -6, y: 18 },    // Left engine inner
      { x: 0, y: 22 },     // Center back
      { x: 6, y: 18 },     // Right engine inner
      { x: 18, y: 20 },    // Right engine outer
      { x: 16, y: 8 },     // Right mid hull
      { x: 20, y: -4 },    // Right wing point
      { x: 10, y: -8 },    // Right upper hull
      { x: 10, y: -16 },   // Right nose angle
    ],
  },

  // Cruiser class - elongated angular hull
  // Sleek but with hard edges
  cruiser: {
    scale: 1.5,
    thrustOffset: { x: 0, y: 26 },
    points: [
      { x: 0, y: -30 },    // Sharp nose
      { x: -8, y: -22 },   // Left nose bevel
      { x: -14, y: -10 },  // Left upper hull
      { x: -18, y: 4 },    // Left mid hull
      { x: -16, y: 16 },   // Left lower hull
      { x: -12, y: 24 },   // Left engine
      { x: 0, y: 22 },     // Center back notch
      { x: 12, y: 24 },    // Right engine
      { x: 16, y: 16 },    // Right lower hull
      { x: 18, y: 4 },     // Right mid hull
      { x: 14, y: -10 },   // Right upper hull
      { x: 8, y: -22 },    // Right nose bevel
    ],
  },

  // Battleship class - heavy wedge with turret platforms
  // Massive angular hull, imposing silhouette
  battleship: {
    scale: 2.0,
    thrustOffset: { x: 0, y: 28 },
    points: [
      { x: 0, y: -28 },    // Armored prow
      { x: -12, y: -24 },  // Left prow angle
      { x: -20, y: -16 },  // Left upper armor
      { x: -24, y: -4 },   // Left turret platform
      { x: -28, y: 8 },    // Left broadside
      { x: -24, y: 20 },   // Left engine mount
      { x: -14, y: 24 },   // Left engine
      { x: 0, y: 20 },     // Center back
      { x: 14, y: 24 },    // Right engine
      { x: 24, y: 20 },    // Right engine mount
      { x: 28, y: 8 },     // Right broadside
      { x: 24, y: -4 },    // Right turret platform
      { x: 20, y: -16 },   // Right upper armor
      { x: 12, y: -24 },   // Right prow angle
    ],
  },

 // Carrier class - Angular hull with C-shaped drone bay cutout
  // Sharp prow, thick hull arms wrapping around circular bay
  // Based on carrier.svg reference image
 // Carrier class - Angular hull with C-shaped drone bay
  // Converted from carrier.svg, nose pointing UP
  carrier: {
    scale: 1.0,
    thrustOffset: { x: 0, y: 36 },
    points: [
      { x: -36, y: 5 },
      { x: -34, y: -1 },
      { x: -33, y: -7 },
      { x: -33, y: -12 },
      { x: -31, y: -18 },
      { x: -29, y: -23 },
      { x: -27, y: -28 },
      { x: -24, y: -33 },
      { x: -20, y: -35 },
      { x: -16, y: -31 },
      { x: -12, y: -27 },
      { x: -8, y: -22 },
      { x: -4, y: -18 },
      { x: -3, y: -13 },
      { x: -5, y: -8 },
      { x: -2, y: -3 },
      { x: 4, y: -4 },
      { x: 5, y: -10 },
      { x: 3, y: -15 },
      { x: 5, y: -20 },
      { x: 9, y: -24 },
      { x: 13, y: -28 },
      { x: 17, y: -32 },
      { x: 21, y: -36 },
      { x: 25, y: -31 },
      { x: 27, y: -27 },
      { x: 30, y: -21 },
      { x: 32, y: -16 },
      { x: 33, y: -10 },
      { x: 33, y: -5 },
      { x: 35, y: 1 },
      { x: 36, y: 6 },
      { x: 32, y: 10 },
      { x: 28, y: 12 },
      { x: 24, y: 16 },
      { x: 19, y: 20 },
      { x: 16, y: 25 },
      { x: 14, y: 30 },
      { x: 10, y: 33 },
      { x: 4, y: 35 },
      { x: 2, y: 32 },
      { x: 2, y: 27 },
      { x: 2, y: 21 },
      { x: -2, y: 22 },
      { x: -2, y: 28 },
      { x: -2, y: 33 },
      { x: -5, y: 35 },
      { x: -11, y: 33 },
      { x: -14, y: 29 },
      { x: -17, y: 24 },
      { x: -20, y: 20 },
      { x: -24, y: 16 },
      { x: -28, y: 11 },
      { x: -32, y: 11 },
      { x: -36, y: 7 },
    ],
  },

  // Dreadnought class - ultimate capital ship
  // Elongated angular hull with straight edges
  dreadnought: {
    scale: 2.2,
    thrustOffset: { x: 0, y: 36 },
    points: [
      { x: 0, y: -40 },    // Sharp prow point
      { x: -8, y: -34 },   // Left prow
      { x: -14, y: -26 },  // Left upper hull
      { x: -14, y: -16 },  // Left hull straight
      { x: -24, y: -12 },  // Left weapon bay outer
      { x: -24, y: 8 },    // Left broadside
      { x: -28, y: 12 },   // Left engine block outer
      { x: -28, y: 26 },   // Left engine outer
      { x: -14, y: 30 },   // Left engine inner
      { x: 0, y: 26 },     // Center back
      { x: 14, y: 30 },    // Right engine inner
      { x: 28, y: 26 },    // Right engine outer
      { x: 28, y: 12 },    // Right engine block outer
      { x: 24, y: 8 },     // Right broadside
      { x: 24, y: -12 },   // Right weapon bay outer
      { x: 14, y: -16 },   // Right hull straight
      { x: 14, y: -26 },   // Right upper hull
      { x: 8, y: -34 },    // Right prow
    ],
  },

  // Hauler class - simple boxy cargo transport
  // Basic rectangle - industrial workhorse
  hauler: {
    scale: 1.8,
    thrustOffset: { x: 0, y: 22 },
    points: [
      { x: -12, y: -20 },  // Left front
      { x: 12, y: -20 },   // Right front
      { x: 12, y: 20 },    // Right back
      { x: -12, y: 20 },   // Left back
    ],
  },
};

// Ship ID to design mapping - maps each specific ship to its class design
// All ships in a class share the same shape, size differences come from ship.size property
// Maps ship class id → SVG/design key. Both legacy ids (sparrow, swift, ...)
// and combat-overhaul canonical ids (corvette_t1, corvette_t2, ...) are listed
// so visuals resolve regardless of which id the DB / server hands us.
export const SHIP_ID_TO_DESIGN: Record<string, string> = {
  // Corvette class
  sparrow: 'corvette',  swift: 'corvette',  falcon: 'corvette',  peregrine: 'corvette',
  corvette_t1: 'corvette', corvette_t2: 'corvette', corvette_t3: 'corvette', corvette_t4: 'corvette',

  // Frigate class
  corsair: 'frigate',  raider: 'frigate',  stalker: 'frigate',  phantom: 'frigate',
  frigate_t1: 'frigate', frigate_t2: 'frigate', frigate_t3: 'frigate', frigate_t4: 'frigate',

  // Destroyer class
  sentinel: 'destroyer',  guardian: 'destroyer',  warden: 'destroyer',  vindicator: 'destroyer',
  destroyer_t1: 'destroyer', destroyer_t2: 'destroyer', destroyer_t3: 'destroyer', destroyer_t4: 'destroyer',

  // Cruiser class
  pioneer: 'cruiser',  voyager: 'cruiser',  pathfinder: 'cruiser',  odyssey: 'cruiser',
  cruiser_t1: 'cruiser', cruiser_t2: 'cruiser', cruiser_t3: 'cruiser', cruiser_t4: 'cruiser',

  // Battleship class
  bulwark: 'battleship',  fortress: 'battleship',  juggernaut: 'battleship',  leviathan: 'battleship',
  battleship_t1: 'battleship', battleship_t2: 'battleship', battleship_t3: 'battleship', battleship_t4: 'battleship',

  // Carrier class
  nest: 'carrier',  hive: 'carrier',  swarm: 'carrier',  armada: 'carrier',
  carrier_t1: 'carrier', carrier_t2: 'carrier', carrier_t3: 'carrier', carrier_t4: 'carrier',

  // Dreadnought class
  titan: 'dreadnought',  colossus: 'dreadnought',  behemoth: 'dreadnought',  sovereign: 'dreadnought',
  dreadnought_t1: 'dreadnought', dreadnought_t2: 'dreadnought', dreadnought_t3: 'dreadnought', dreadnought_t4: 'dreadnought',

  // Hauler class
  mule: 'hauler',  nomad: 'hauler',  caravan: 'hauler',  mammoth: 'hauler',
  hauler_t1: 'hauler', hauler_t2: 'hauler', hauler_t3: 'hauler', hauler_t4: 'hauler',
};

// Size-based scale multipliers
// Ships have a 'size' property in their definition (small/medium/large)
// This multiplier adjusts the base design scale
export const SIZE_SCALE_MULTIPLIERS: Record<string, number> = {
  small: 0.85,
  medium: 1.0,
  large: 1.25,
};

// Pre-rendered thrust frame textures (generated once per game session)
const thrustTexturesGenerated = new Set<string>();
const THRUST_FRAME_COUNT = 4;

export interface ShipGraphicsOptions {
  color?: number;
  size?: 'small' | 'medium' | 'large';
  tier?: 1 | 2 | 3 | 4;  // Ship tier affects glow intensity
}

export class ShipGraphics extends Container {
  // SVG mode (when SVG texture is available)
  private usingSVG: boolean = false;
  private svgShipSprite: Sprite | null = null;
  private glowFilter: GlowFilter | null = null;
  private damageGlowFilter: GlowFilter | null = null;
  private _damageGlowActive: boolean = false;
  private designKey: string = '';

  // Point-based mode (fallback)
  private glowGraphics: Graphics;  // Glow layer (behind ship)
  private graphics: Graphics;       // Ship body

  // Shared
  private thrustSprite: Sprite;
  private engineFlare: EngineFlare;
  private design: ShipDesign;
  private shipClassId: string;
  private _thrusting: boolean = false;
  private thrustFlicker: number = 0;
  private _shipColor: number = DEFAULT_SHIP_COLOR;
  private thrustTextures: Texture[] = [];
  private sizeMultiplier: number = 1.0;
  private _tier: 1 | 2 | 3 | 4 = 1;
  private pulseTime: number = 0;  // For T4 pulse animation

  // Visibility helpers
  setVisible(value: boolean): this {
    this.visible = value;
    return this;
  }

  setAlpha(value: number): this {
    this.alpha = value;
    return this;
  }

  setScale(x: number, y?: number): this {
    this.scale.set(x, y ?? x);
    return this;
  }

  setPosition(x: number, y: number): this {
    this.position.set(x, y);
    return this;
  }

  setRotation(radians: number): this {
    this.rotation = radians;
    return this;
  }

  setDamageGlow(active: boolean): void {
    this._damageGlowActive = active;
    if (this.usingSVG && this.svgShipSprite) {
      if (active) {
        if (!this.damageGlowFilter) {
          this.damageGlowFilter = new GlowFilter({
            distance: 10,
            outerStrength: 3,
            innerStrength: 2,
            color: 0xff2200,
            quality: 0.3,
          });
        }
        // Stack damage glow with existing glow filter
        const filters: any[] = [];
        if (this.glowFilter) filters.push(this.glowFilter);
        filters.push(this.damageGlowFilter);
        this.svgShipSprite.filters = filters;
      } else {
        // Restore normal filter
        this.svgShipSprite.filters = this.glowFilter ? [this.glowFilter] : [];
      }
    } else if (!this.usingSVG) {
      // Point-based mode: tint the graphics red briefly
      if (active) {
        this.graphics.tint = 0xff2200;
      } else {
        this.graphics.tint = 0xffffff;
      }
    }
  }

  constructor(x: number, y: number, shipClassId: string, options?: ShipGraphicsOptions | number) {
    super();

    this.position.set(x, y);
    this.shipClassId = shipClassId;

    // Handle legacy color-only parameter or new options object
    let color: number | undefined;
    let size: 'small' | 'medium' | 'large' = 'medium';
    let tier: 1 | 2 | 3 | 4 = 1;

    if (typeof options === 'number') {
      // Legacy: just color
      color = options;
    } else if (options) {
      color = options.color;
      size = options.size || 'medium';
      tier = options.tier || 1;
    }

    if (color !== undefined) {
      this._shipColor = color;
    }
    this._tier = tier;

    // Calculate size multiplier based on ship size category
    this.sizeMultiplier = SIZE_SCALE_MULTIPLIERS[size] || 1.0;

    // Get the design for this ship (using mapping, fallback to direct match, then corvette)
    this.designKey = SHIP_ID_TO_DESIGN[shipClassId] || shipClassId;
    this.design = SHIP_DESIGNS[this.designKey] || SHIP_DESIGNS.corvette;

    // Check if SVG texture is available (preloaded during boot)
    const svgTexture = getSVGTexture(this.designKey);
    this.usingSVG = svgTexture !== null;

    // Create glow graphics layer (for point-based fallback)
    this.glowGraphics = new Graphics();

    // Create graphics for ship body (for point-based fallback)
    this.graphics = new Graphics();

    // Create sprite for thrust (shared by both modes)
    this.thrustSprite = new Sprite();
    this.thrustSprite.anchor.set(0.5, 0);
    this.thrustSprite.visible = false;

    // Create volumetric engine flare
    this.engineFlare = new EngineFlare(this.designKey, this.sizeMultiplier);

    if (this.usingSVG && svgTexture) {
      // SVG MODE: Use sprites for ship rendering with GlowFilter
      // SVG ships point RIGHT, we need them to point UP, so rotate -90°
      const svgMeta = SVG_SHIP_META[this.designKey] || SVG_SHIP_META.corvette;
      const totalScale = svgMeta.scale * this.sizeMultiplier;
      const glowSettings = TIER_GLOW_SETTINGS[this._tier] || TIER_GLOW_SETTINGS[1];

      // Create main ship sprite (untinted to show original SVG colors)
      this.svgShipSprite = new Sprite(svgTexture);
      this.svgShipSprite.anchor.set(0.5);
      this.svgShipSprite.scale.set(totalScale);
      this.svgShipSprite.rotation = SVG_ROTATION_OFFSET; // Rotate to point UP

      // Apply GlowFilter to make the SVG lines glow (not a circular halo)
      // The filter makes the actual strokes/fills emit light
      this.glowFilter = new GlowFilter({
        distance: glowSettings.wideDistance,
        outerStrength: glowSettings.wideStrength * 2,
        innerStrength: glowSettings.tightStrength,
        color: this._shipColor,
        quality: 0.3,
      });
      this.svgShipSprite.filters = [this.glowFilter];

      // Position engine flare behind the ship (rendered before ship so it's behind)
      this.engineFlare.position.set(0, svgMeta.thrustOffsetY * totalScale);
      this.addChild(this.engineFlare);

      this.addChild(this.svgShipSprite);

      // Position thrust sprite behind the ship (at the rear after -90° rotation)
      // In the rotated coordinate system, +y is DOWN (behind the ship)
      this.thrustSprite.position.set(0, svgMeta.thrustOffsetY * totalScale);
      this.addChild(this.thrustSprite);
    } else {
      // POINT-BASED MODE: Use Graphics for rendering
      const totalScale = this.design.scale * this.sizeMultiplier;
      this.thrustSprite.position.set(
        this.design.thrustOffset.x * totalScale,
        this.design.thrustOffset.y * totalScale
      );

      // Position engine flare at thrust offset
      this.engineFlare.position.set(
        this.design.thrustOffset.x * totalScale,
        this.design.thrustOffset.y * totalScale
      );

      // Add in correct order: glow behind, flare behind ship, ship on top, thrust on top
      this.addChild(this.glowGraphics);
      this.addChild(this.engineFlare);
      this.addChild(this.graphics);
      this.addChild(this.thrustSprite);

      // Draw the ship using points
      this.drawShip();
    }
  }

  /**
   * Generate thrust textures (call after renderer is available)
   */
  generateThrustTextures(): void {
    // Generate volumetric engine flare textures
    this.engineFlare.generateTextures();

    const textureKey = `thrust-${this.shipClassId}`;
    if (thrustTexturesGenerated.has(textureKey)) {
      // Load existing textures
      for (let frame = 0; frame < THRUST_FRAME_COUNT; frame++) {
        const key = `thrust-${this.shipClassId}-${frame}`;
        const texture = assetLoader.getTexture(key);
        if (texture) {
          this.thrustTextures.push(texture);
        }
      }
      if (this.thrustTextures.length > 0) {
        this.thrustSprite.texture = this.thrustTextures[0];
      }
      return;
    }

    // Generate multiple flame frames with different heights
    for (let frame = 0; frame < THRUST_FRAME_COUNT; frame++) {
      const graphics = new Graphics();
      const flameHeight = 16 + (frame * 3); // 16, 19, 22, 25

      // Outer flame (orange from palette)
      // PixiJS 8 expects flat array [x1, y1, x2, y2, ...] for poly()
      graphics.poly([16 - 8, 0, 16, flameHeight, 16 + 8, 0], true);
      graphics.fill({ color: 0xdd5639, alpha: 0.8 });

      // Inner flame (teal from palette)
      graphics.poly([16 - 4, 0, 16, flameHeight * 0.6, 16 + 4, 0], true);
      graphics.fill({ color: 0x20a5a6, alpha: 0.9 });

      const frameKey = `thrust-${this.shipClassId}-${frame}`;
      const texture = assetLoader.generateTexture(graphics, frameKey);
      this.thrustTextures.push(texture);
      graphics.destroy();
    }

    if (this.thrustTextures.length > 0) {
      this.thrustSprite.texture = this.thrustTextures[0];
    }

    thrustTexturesGenerated.add(textureKey);
  }

  /**
   * Apply a tint to the ship sprite for overload effect.
   * Pass 0xffffff to reset to normal.
   */
  setOverloadTint(tint: number): void {
    if (this.usingSVG && this.svgShipSprite) {
      this.svgShipSprite.tint = tint;
    } else if (!this.usingSVG) {
      this.graphics.tint = tint;
    }
  }

  get shipColor(): number {
    return this._shipColor;
  }

  set shipColor(color: number) {
    this._shipColor = color;
    if (this.usingSVG) {
      // Update GlowFilter color
      if (this.glowFilter) {
        this.glowFilter.color = color;
      }
      // Main ship sprite stays untinted to show original SVG colors
    } else {
      this.drawShip();
    }
  }

  private drawShip(): void {
    // Only used in point-based mode
    if (this.usingSVG) return;

    this.graphics.clear();
    this.glowGraphics.clear();

    // Apply BOTH design.scale (base size for ship class) AND sizeMultiplier (size/tier adjustment)
    const totalScale = this.design.scale * this.sizeMultiplier;
    const flatPoints: number[] = [];
    for (const p of this.design.points) {
      flatPoints.push(p.x * totalScale, p.y * totalScale);
    }

    // Get tier glow settings
    const glowSettings = TIER_GLOW_SETTINGS[this._tier] || TIER_GLOW_SETTINGS[1];

    // Draw glow layers (multiple strokes with decreasing alpha for soft glow effect)
    // Wide glow (outermost)
    this.glowGraphics.poly(flatPoints, true);
    this.glowGraphics.stroke({
      width: glowSettings.wideDistance,
      color: this._shipColor,
      alpha: glowSettings.wideStrength
    });

    // Medium glow
    this.glowGraphics.poly(flatPoints, true);
    this.glowGraphics.stroke({
      width: glowSettings.tightDistance * 2,
      color: this._shipColor,
      alpha: glowSettings.tightStrength * 0.7
    });

    // Tight glow (closest to ship)
    this.glowGraphics.poly(flatPoints, true);
    this.glowGraphics.stroke({
      width: glowSettings.tightDistance,
      color: this._shipColor,
      alpha: glowSettings.tightStrength
    });

    // Draw ship body with solid fill and stroke
    this.graphics.poly(flatPoints, true);
    this.graphics.fill({ color: this._shipColor, alpha: 0.15 });  // Subtle fill
    this.graphics.stroke({ width: 2, color: this._shipColor });
  }

  get thrusting(): boolean {
    return this._thrusting;
  }

  set thrusting(value: boolean) {
    this._thrusting = value;
    // Old triangle sprite disabled — replaced by volumetric engine flare
    this.thrustSprite.visible = false;
    if (value) {
      this.engineFlare.show();
    } else {
      this.engineFlare.hide();
    }
  }

  setFlareIntensity(factor: number): void {
    this.engineFlare.setIntensity(factor);
  }

  update(): void {
    // Animate thrust flame by cycling through pre-rendered frames
    if (this._thrusting && this.thrustTextures.length > 0) {
      this.thrustFlicker++;
      if (this.thrustFlicker % 3 === 0) {
        // Cycle through thrust frames (0-3)
        const frame = Math.floor(this.thrustFlicker / 3) % THRUST_FRAME_COUNT;
        this.thrustSprite.texture = this.thrustTextures[frame];
      }
    }

    // Animate volumetric engine flare
    if (this._thrusting) {
      this.engineFlare.update();
    }

    // T4 pulse animation - subtle glow intensity pulse
    const glowSettings = TIER_GLOW_SETTINGS[this._tier];
    if (glowSettings && glowSettings.pulse) {
      this.pulseTime += 0.03;
      // Subtle pulse between 0.85 and 1.15
      const pulseIntensity = 1 + Math.sin(this.pulseTime) * 0.15;

      if (this.usingSVG && this.glowFilter) {
        // Pulse GlowFilter strength
        this.glowFilter.outerStrength = glowSettings.wideStrength * 2 * pulseIntensity;
      } else {
        this.glowGraphics.alpha = pulseIntensity;
      }
    }
  }

  getDesign(): ShipDesign {
    return this.design;
  }

  // Get bounding box size for physics
  getShipBounds(): { width: number; height: number } {
    if (this.usingSVG) {
      // Use SVG metadata for bounds
      const svgMeta = SVG_SHIP_META[this.designKey] || SVG_SHIP_META.corvette;
      const totalScale = svgMeta.scale * this.sizeMultiplier;
      return {
        width: svgMeta.width * totalScale,
        height: svgMeta.height * totalScale,
      };
    }

    // Point-based bounds calculation
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    for (const point of this.design.points) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }

    // Include both design scale and size multiplier
    const totalScale = this.design.scale * this.sizeMultiplier;
    return {
      width: (maxX - minX) * totalScale,
      height: (maxY - minY) * totalScale,
    };
  }
}

// Debris piece for destruction animation
export class ShipDebris extends Graphics {
  private velocityX: number;
  private velocityY: number;
  private angularVelocity: number;
  private lifetime: number = 3500; // Longer debris lifetime for more visible effect
  private startTime: number;
  private debrisPoints: { x: number; y: number }[];
  private debrisColor: number;

  constructor(
    x: number,
    y: number,
    points: { x: number; y: number }[],
    color: number,
    explosionAngle: number,
    baseRotation: number
  ) {
    super();
    this.position.set(x, y);
    this.debrisPoints = points;
    this.debrisColor = color;
    this.startTime = Date.now();

    // Random velocity away from center - slower for more dramatic effect
    const speed = 60 + Math.random() * 120;
    const angle = explosionAngle + (Math.random() - 0.5) * 1.5;
    this.velocityX = Math.cos(angle) * speed;
    this.velocityY = Math.sin(angle) * speed;
    this.angularVelocity = (Math.random() - 0.5) * 300;

    this.rotation = baseRotation;
    this.drawPiece();
  }

  private drawPiece(): void {
    this.clear();
    // PixiJS 8 expects flat array [x1, y1, x2, y2, ...] for poly()
    const flatPoints: number[] = [];
    for (const p of this.debrisPoints) {
      flatPoints.push(p.x, p.y);
    }
    this.poly(flatPoints, true);
    this.stroke({ width: 2, color: this.debrisColor });
  }

  update(delta: number): boolean {
    const elapsed = Date.now() - this.startTime;
    if (elapsed > this.lifetime) {
      this.destroy();
      return false;
    }

    // Apply velocity
    const dt = delta / 1000;
    this.x += this.velocityX * dt;
    this.y += this.velocityY * dt;
    this.rotation += (this.angularVelocity * Math.PI / 180) * dt;

    // Fade out
    const alpha = 1 - (elapsed / this.lifetime);
    this.alpha = alpha;

    // Slow down
    this.velocityX *= 0.98;
    this.velocityY *= 0.98;

    return true;
  }
}

// Create explosion effect with debris
export function createShipExplosion(
  parent: Container,
  x: number,
  y: number,
  rotation: number,
  shipClassId: string,
  color: number = DEFAULT_SHIP_COLOR
): ShipDebris[] {
  const designKey = SHIP_ID_TO_DESIGN[shipClassId] || shipClassId;
  const design = SHIP_DESIGNS[designKey] || SHIP_DESIGNS.corvette;
  const debris: ShipDebris[] = [];

  // Split ship into triangular debris pieces
  const centerX = design.points.reduce((sum, p) => sum + p.x, 0) / design.points.length;
  const centerY = design.points.reduce((sum, p) => sum + p.y, 0) / design.points.length;

  for (let i = 0; i < design.points.length; i++) {
    const p1 = design.points[i];
    const p2 = design.points[(i + 1) % design.points.length];

    // Create triangular debris from center to edge
    const piecePoints = [
      { x: centerX, y: centerY },
      { x: p1.x, y: p1.y },
      { x: p2.x, y: p2.y },
    ];

    // Calculate piece center
    const pieceCenterX = (centerX + p1.x + p2.x) / 3;
    const pieceCenterY = (centerY + p1.y + p2.y) / 3;

    // Rotate center point by ship rotation
    const rotRad = rotation;
    const rotatedX = pieceCenterX * Math.cos(rotRad) - pieceCenterY * Math.sin(rotRad);
    const rotatedY = pieceCenterX * Math.sin(rotRad) + pieceCenterY * Math.cos(rotRad);

    // Explosion angle from center
    const explosionAngle = Math.atan2(rotatedY, rotatedX);

    const debrisPiece = new ShipDebris(
      x + rotatedX,
      y + rotatedY,
      piecePoints,
      color,
      explosionAngle,
      rotation
    );
    parent.addChild(debrisPiece);
    debris.push(debrisPiece);
  }

  // Add flash effect
  const flash = new Graphics();
  flash.position.set(x, y);
  flash.circle(0, 0, 40);
  flash.fill({ color: 0xffffff, alpha: 0.8 });
  flash.blendMode = 'add';
  parent.addChild(flash);

  tween({
    targets: flash,
    alpha: 0,
    duration: 200,
    ease: 'power2.out',
    onComplete: () => flash.destroy(),
  });

  // Clean up debris after animation
  delay(4000, () => {
    for (const d of debris) {
      if (!d.destroyed) {
        d.destroy();
      }
    }
  });

  return debris;
}

/**
 * Asteroids-style explosion: splits the actual SVG texture into a grid of fragments
 * that drift outward, rotating and fading. The real ship pieces float into space.
 */
export function createSVGExplosion(
  parent: Container,
  x: number,
  y: number,
  rotation: number,
  shipClassId: string,
  color: number = DEFAULT_SHIP_COLOR,
): void {
  const designKey = SHIP_ID_TO_DESIGN[shipClassId] || shipClassId;
  const svgMeta = SVG_SHIP_META[designKey];
  const texture = getSVGTexture(designKey);
  if (!texture || !svgMeta) {
    // Fallback to old explosion
    createShipExplosion(parent, x, y, rotation, shipClassId, color);
    return;
  }

  const scale = svgMeta.scale;
  const texW = texture.width;
  const texH = texture.height;

  // Grid size — more pieces for bigger ships
  const cols = texW > 250 ? 4 : 3;
  const rows = texH > 250 ? 4 : 3;
  const cellW = texW / cols;
  const cellH = texH / rows;

  // Ship rotation offset (SVGs point right, game rotates -90 to point up, plus ship heading)
  const shipRot = rotation + SVG_ROTATION_OFFSET;

  const fragments: Container[] = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      // Sub-rectangle in texture space
      const frame = new PIXI_Rectangle(c * cellW, r * cellH, cellW, cellH);
      const fragTexture = new Texture({ source: texture.source, frame });

      const sprite = new Sprite(fragTexture);
      sprite.anchor.set(0.5);
      sprite.scale.set(scale);

      // Position of this fragment in local ship space (centered around ship origin)
      const localX = (c + 0.5 - cols / 2) * cellW * scale;
      const localY = (r + 0.5 - rows / 2) * cellH * scale;

      // Rotate local position by ship heading
      const cos = Math.cos(shipRot);
      const sin = Math.sin(shipRot);
      const worldLocalX = localX * cos - localY * sin;
      const worldLocalY = localX * sin + localY * cos;

      sprite.position.set(x + worldLocalX, y + worldLocalY);
      sprite.rotation = shipRot;

      // Drift direction: outward from center
      const driftAngle = Math.atan2(worldLocalY, worldLocalX) + (Math.random() - 0.5) * 0.5;
      const driftSpeed = 30 + Math.random() * 60;
      const angularSpeed = (Math.random() - 0.5) * 2; // radians per second

      sprite.zIndex = 80;
      parent.addChild(sprite);
      fragments.push(sprite);

      // Animate: drift + rotate + fade
      const startTime = Date.now();
      const lifetime = 2500 + Math.random() * 1500;
      const vx = Math.cos(driftAngle) * driftSpeed;
      const vy = Math.sin(driftAngle) * driftSpeed;

      let rafId: number | null = null;
      const tick = () => {
        const elapsed = Date.now() - startTime;
        if (elapsed > lifetime || sprite.destroyed) {
          if (!sprite.destroyed) sprite.destroy();
          fragTexture.destroy();
          rafId = null;
          return;
        }
        const dt = 1 / 60;
        sprite.x += vx * dt;
        sprite.y += vy * dt;
        sprite.rotation += angularSpeed * dt;
        sprite.alpha = 1 - (elapsed / lifetime);
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }
  }

  // Brief flash at center
  const flash = new Graphics();
  flash.position.set(x, y);
  flash.circle(0, 0, 30 * scale);
  flash.fill({ color: 0xffffff, alpha: 0.6 });
  flash.blendMode = 'add';
  flash.zIndex = 81;
  parent.addChild(flash);
  tween({
    targets: flash,
    alpha: 0,
    duration: 400,
    onComplete: () => flash.destroy(),
  });
}

/**
 * Asteroids-style shield shatter: the shield circle breaks into arc-shaped
 * fragments that drift outward, spinning and fading — like shattering glass.
 */
export function createShieldShatter(
  parent: Container,
  cx: number,
  cy: number,
  radius: number,
  coverageDeg: number = 360,
  noseAngle: number = 0,
): void {
  // Scale shard count with coverage (fewer shards for smaller arcs)
  const SHARD_COUNT = Math.max(4, Math.floor((10 + Math.random() * 4) * (coverageDeg / 360)));
  const LIFETIME_BASE = 1800;
  const LIFETIME_RAND = 800;
  const SHIELD_COLOR = 0x88ddff;
  const SHIELD_FILL = 0x66ccff;

  // Coverage arc range (centered on noseAngle)
  const coverageRad = coverageDeg * (Math.PI / 180);
  const arcRangeStart = coverageDeg >= 360 ? 0 : noseAngle - coverageRad / 2;
  const arcRangeTotal = coverageDeg >= 360 ? Math.PI * 2 : coverageRad;

  for (let i = 0; i < SHARD_COUNT; i++) {
    // Each shard is an arc segment within the coverage range
    const arcStart = arcRangeStart + (arcRangeTotal * i) / SHARD_COUNT;
    const arcEnd = arcStart + (arcRangeTotal / SHARD_COUNT) * (0.6 + Math.random() * 0.35);
    // Slight radius variation per shard
    const r = radius * (0.95 + Math.random() * 0.1);

    const shard = new Graphics();
    // Draw the arc shard: two concentric arcs connected to form a curved strip
    const innerR = r - 3 - Math.random() * 2;
    const outerR = r + 1 + Math.random() * 2;
    const segments = 6;

    const points: number[] = [];
    // Outer arc (forward)
    for (let s = 0; s <= segments; s++) {
      const t = s / segments;
      const a = arcStart + (arcEnd - arcStart) * t;
      points.push(Math.cos(a) * outerR, Math.sin(a) * outerR);
    }
    // Inner arc (backward)
    for (let s = segments; s >= 0; s--) {
      const t = s / segments;
      const a = arcStart + (arcEnd - arcStart) * t;
      points.push(Math.cos(a) * innerR, Math.sin(a) * innerR);
    }
    shard.poly(points, true);
    shard.fill({ color: SHIELD_FILL, alpha: 0.25 });
    shard.stroke({ width: 1.5, color: SHIELD_COLOR, alpha: 0.8 });

    shard.position.set(cx, cy);
    shard.zIndex = 19;
    parent.addChild(shard);

    // Drift direction: outward from the arc midpoint
    const midAngle = (arcStart + arcEnd) / 2 + (Math.random() - 0.5) * 0.3;
    const driftSpeed = 40 + Math.random() * 80;
    const vx = Math.cos(midAngle) * driftSpeed;
    const vy = Math.sin(midAngle) * driftSpeed;
    const angularSpeed = (Math.random() - 0.5) * 2.5; // rad/s

    const startTime = Date.now();
    const lifetime = LIFETIME_BASE + Math.random() * LIFETIME_RAND;

    let rafId: number | null = null;
    const tick = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed > lifetime || shard.destroyed) {
        if (!shard.destroyed) shard.destroy();
        rafId = null;
        return;
      }
      const dt = 1 / 60;
      shard.x += vx * dt;
      shard.y += vy * dt;
      shard.rotation += angularSpeed * dt;
      shard.alpha = 1 - (elapsed / lifetime);
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
  }

  // Brief cyan flash at center
  const flash = new Graphics();
  flash.position.set(cx, cy);
  flash.circle(0, 0, radius * 0.6);
  flash.fill({ color: SHIELD_COLOR, alpha: 0.3 });
  flash.blendMode = 'add';
  flash.zIndex = 20;
  parent.addChild(flash);
  tween({
    targets: flash,
    alpha: 0,
    duration: 300,
    onComplete: () => flash.destroy(),
  });
}
